import type { ApiResponse, Session } from '@/types/global'
import axios from '../plugins/axios'
import { context } from '.'
import { toUserFriendlyError } from '@/utils/error-message'

export const fetchSessions = async (fields?: string[], filter: any[] = []) => {
  const response = await axios.post<ApiResponse<Session[]>>(
    `/api-proxy/${context}/api/1.0/session`,
    {
      jsonrpc: '2.0',
      params: {
        fields,
        filter
      }
    }
  )

  if (response.data.error) {
    throw toUserFriendlyError(response.data.error, 'تعذر تحميل الحلقات. يرجى المحاولة مرة أخرى.')
  }

  return Array.isArray(response.data.result) ? response.data.result : []
}

export const fetchSession = async (id: number, fields?: string[]) => {
  const response = await axios.post<ApiResponse<Session>>(
    `/api-proxy/${context}/api/${import.meta.env.VITE_API_VERSION}/session/${id}`,
    {
      jsonrpc: '2.0',
      params: {
        fields
      }
    }
  )

  return response.data.result
}

export type SessionStartStateResult = {
  id: number
  is_started: boolean
  last_start_date: string
  last_closed_date: string
}

const updateSessionStartState = async (sessionId: number, action: 'start' | 'close') => {
  const response = await axios.post<ApiResponse<SessionStartStateResult>>(
    `/api-proxy/${context}/api/${import.meta.env.VITE_API_VERSION}/session/${action}`,
    {
      jsonrpc: '2.0',
      params: {
        session_id: sessionId
      }
    }
  )

  if (response.data.error) {
    throw toUserFriendlyError(response.data.error)
  }

  return response.data.result
}

export const startSession = async (sessionId: number) =>
  await updateSessionStartState(sessionId, 'start')

export const closeSession = async (sessionId: number) =>
  await updateSessionStartState(sessionId, 'close')

export type SessionLifecycleResult = {
  status?: boolean
  message?: string
}

async function updateSessionLifecycle(
  action: 'delete' | 'archive' | 'unarchive',
  ids: number | number[]
) {
  const response = await axios.post<ApiResponse<SessionLifecycleResult>>(
    `/api-proxy/${context}/api/1.0/session/${action}`,
    {
      jsonrpc: '2.0',
      params: {
        ids: Array.isArray(ids) ? ids : [ids]
      }
    }
  )

  if (response.data.error) {
    throw toUserFriendlyError(response.data.error)
  }

  if (response.data.result?.status === false) {
    throw toUserFriendlyError(response.data.result.message, 'تعذر تنفيذ الإجراء على الحلقة')
  }

  return response.data.result
}

export const deleteSessions = (ids: number | number[]) => updateSessionLifecycle('delete', ids)

export const archiveSessions = (ids: number | number[]) => updateSessionLifecycle('archive', ids)

export const unarchiveSessions = (ids: number | number[]) =>
  updateSessionLifecycle('unarchive', ids)

export type CreateSessionParams = {
  active: boolean
  name: string
  gender: 'male' | 'female'
  period: 'fajr' | 'thuhr' | 'asr' | 'maghrib' | 'ishaa'
  specified_time?: boolean
  start_time: number
  session_time_from?: number
  session_time_to?: number
  teacher_id: number
  specialist_teacher_id?: number
  session_admin_id: number
  daily_evaluation_sections: '1' | '2' | '3'
  request_type_id: number
  group_day_id?: number
  daily_recitation_plan_id?: number
  recitation_start_id?: number
  daily_revision_plan_id?: number
  revision_start_id?: number
}

export const createSession = async (params: CreateSessionParams) => {
  const response = await axios.post<ApiResponse<any>>(
    `/api-proxy/${context}/api/1.0/session/create`,
    {
      jsonrpc: '2.0',
      params
    }
  )

  if (response.data.error) {
    throw toUserFriendlyError(response.data.error)
  }

  const result = response.data.result as any
  if (result && typeof result === 'object' && result.status === false && result.message) {
    throw toUserFriendlyError(result.message, 'تعذر إنشاء الحلقة')
  }

  return response.data.result
}

export type RequestType = {
  id: number
  display_name: string
}

export const fetchRequestTypes = async () => {
  const response = await axios.post<ApiResponse<RequestType[]>>(
    `/api-proxy/${context}/api/1.0/fetch/request_type`,
    {
      jsonrpc: '2.0',
      params: {}
    }
  )

  if (response.data.error) {
    throw toUserFriendlyError(response.data.error)
  }

  return response.data.result
}

export type Day = {
  id: number
  display_name: string
}

export const fetchDays = async () => {
  const response = await axios.post<ApiResponse<Day[]>>(
    `/api-proxy/${context}/api/1.0/fetch/days`,
    {
      jsonrpc: '2.0',
      params: {}
    }
  )

  if (response.data.error) {
    throw toUserFriendlyError(response.data.error)
  }

  return response.data.result
}
